import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Plus, Edit, Trash2, Eye, Calendar, CheckCircle, XCircle } from 'lucide-react';
import { ThemeContext, API_URL } from './App';

function AdminBlog() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showGenerator, setShowGenerator] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [generateTitle, setGenerateTitle] = useState('');
    const [generatePrompt, setGeneratePrompt] = useState('');
    const { themeStyles } = React.useContext(ThemeContext);

    useEffect(() => {
        fetchPosts();
    }, []);

    const fetchPosts = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/admin/blog/posts`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPosts(response.data.posts || []);
        } catch (error) {
            console.error('Failed to fetch posts:', error);
        } finally {
            setLoading(false);
        }
    };

    const generatePost = async () => {
        if (!generateTitle && !generatePrompt) {
            alert('Please enter a title or prompt');
            return;
        }
        
        setGenerating(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(`${API_URL}/blog/generate`, {
                title: generateTitle,
                prompt: generatePrompt
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                alert('Blog post generated successfully!');
                setShowGenerator(false);
                setGenerateTitle('');
                setGeneratePrompt('');
                fetchPosts();
            }
        } catch (error) {
            console.error('Generation failed:', error);
            alert('Failed to generate blog post');
        } finally {
            setGenerating(false);
        }
    };

    const deletePost = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API_URL}/admin/blog/posts/${id}`, {
            headers: { Authorization: `Bearer ${token}` }
        });
        fetchPosts();
    } catch (error) {
        console.error('Delete failed:', error);
        alert('Failed to delete post');
    }
};

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString();
    };

    const getStatusBadge = (status) => {
        if (status === 'published') {
            return <span className="flex items-center text-green-600 text-sm"><CheckCircle className="w-3 h-3 mr-1" /> Published</span>;
        } else if (status === 'scheduled') {
            return <span className="flex items-center text-amber-600 text-sm"><Calendar className="w-3 h-3 mr-1" /> Scheduled</span>;
        }
        return <span className="flex items-center text-gray-500 text-sm"><XCircle className="w-3 h-3 mr-1" /> Draft</span>;
    };

    return (
        <div className={`min-h-screen p-6 ${themeStyles.background}`}>
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <h1 className="text-2xl font-bold">Blog Management</h1>
                    <button
                        onClick={() => setShowGenerator(!showGenerator)}
                        className={`px-4 py-2 ${themeStyles.button} text-white rounded-lg flex items-center gap-2`}
                    >
                        <Plus className="w-4 h-4" />
                        {showGenerator ? 'Cancel' : 'Generate New Post'}
                    </button>
                </div>

                {/* AI Generator Panel */}
                {showGenerator && (
                    <div className={`mb-8 p-6 ${themeStyles.card} rounded-xl border ${themeStyles.border}`}>
                        <h2 className="text-lg font-semibold mb-4">AI Blog Generator</h2>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-2">Title (Optional)</label>
                                <input
                                    type="text"
                                    value={generateTitle}
                                    onChange={(e) => setGenerateTitle(e.target.value)}
                                    placeholder="e.g., Understanding God's Grace"
                                    className={`w-full px-4 py-2 ${themeStyles.input} rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/50`}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-2">Custom Prompt (Optional)</label>
                                <textarea
                                    value={generatePrompt}
                                    onChange={(e) => setGeneratePrompt(e.target.value)}
                                    placeholder="Write a detailed prompt for the AI to follow..."
                                    rows={4}
                                    className={`w-full px-4 py-2 ${themeStyles.input} rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500/50`}
                                />
                            </div>
                            <button
                                onClick={generatePost}
                                disabled={generating}
                                className={`px-6 py-2 ${themeStyles.button} text-white rounded-lg disabled:opacity-50`}
                            >
                                {generating ? 'Generating...' : 'Generate Blog Post'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Posts List */}
                {loading ? (
                    <div className="flex justify-center py-12">
                        <div className={`w-8 h-8 border-2 ${themeStyles.accent} border-t-transparent rounded-full animate-spin`}></div>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {posts.map((post) => (
                            <div key={post.id} className={`p-4 ${themeStyles.card} rounded-xl border ${themeStyles.border} flex items-center justify-between`}>
                                <div className="flex-1">
                                    <div className="flex items-center gap-3 mb-2">
                                        {getStatusBadge(post.status)}
                                        <span className={`text-xs ${themeStyles.secondaryText}`}>
                                            {formatDate(post.published_at || post.created_at)}
                                        </span>
                                    </div>
                                    <h3 className="font-semibold mb-1">{post.title}</h3>
                                    <p className={`text-sm ${themeStyles.secondaryText} line-clamp-2`}>
                                        {post.excerpt}
                                    </p>
                                </div>
                                <div className="flex gap-2 ml-4">
                                    <a
                                        href={`/blog/${post.slug}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`p-2 ${themeStyles.hover} rounded-lg transition-colors`}
                                        title="View"
                                    >
                                        <Eye className="w-4 h-4" />
                                    </a>
                                    <button
                                        onClick={() => deletePost(post.id)}
                                        className="p-2 hover:bg-red-100 text-red-500 rounded-lg transition-colors"
                                        title="Delete"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

export default AdminBlog;